import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import type { MoodPoint } from '@/types/analytics';
import { formatShortDate } from '@/lib/analytics-format';

interface MoodTrendChartProps {
  data: MoodPoint[];
}

const chartConfig: ChartConfig = {
  avg_score: { label: 'Mood', theme: { light: 'var(--chart-1)', dark: 'var(--chart-1)' } },
};

export function MoodTrendChart({ data }: MoodTrendChartProps) {
  const hasSamples = data.some((d) => d.samples > 0);
  if (!hasSamples) {
    return (
      <div className="flex h-[200px] items-center justify-center text-sm text-muted-foreground">
        No reflections yet
      </div>
    );
  }
  // Null out empty buckets so the line has gaps instead of 0-floor artifacts.
  const points = data.map((d) => ({
    ...d,
    label: formatShortDate(d.bucket),
    value: d.samples > 0 ? d.avg_score : null,
  }));
  return (
    <ChartContainer config={chartConfig} className="h-[200px] w-full">
      <LineChart data={points} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={11} />
        <YAxis
          domain={[1, 5]}
          ticks={[1, 2, 3, 4, 5]}
          tickLine={false}
          axisLine={false}
          fontSize={11}
        />
        <ChartTooltip content={<ChartTooltipContent indicator="dot" />} />
        <Line
          type="monotone"
          dataKey="value"
          name="Mood"
          stroke="var(--color-avg_score)"
          strokeWidth={2}
          dot={{ r: 3 }}
          connectNulls
        />
      </LineChart>
    </ChartContainer>
  );
}
